type ProductWithCategory = {
  id?: string | number;
  category?: { id?: string | number } | null;
  categories?: Array<string | number> | null;
};

type MostRelevantMembershipParams = {
  productId?: string | number | null;
  productCategoryId?: string | number | null;
  targetCategoryId?: string | number | null;
  productCategories?: Array<string | number> | null;
  categoryProductIds?: Array<string | number> | null;
  isChild?: boolean;
};

/**
 * Returns true when the product can be evaluated for "most relevant" membership.
 * Missing products (EAN not found) or products without category must be skipped
 * so the batch does not crash and block remaining EANs.
 */
export function canEvaluateMostRelevantProduct(
  product: ProductWithCategory | null | undefined
): product is ProductWithCategory & { category: { id: string | number } } {
  return product != null && product.category?.id != null;
}

function toIdSet(ids?: Array<string | number> | null): Set<string> {
  return new Set((ids || []).filter((id) => id != null).map((id) => String(id)));
}

/**
 * A product belongs to the target category for "most relevant" when:
 * - its primary category matches, or
 * - it is linked via category_product / product.categories (collections), or
 * - its primary category is a child of the target in the category tree.
 */
export function belongsToMostRelevantCategory(
  params: MostRelevantMembershipParams
): boolean {
  const {
    productId,
    productCategoryId,
    targetCategoryId,
    productCategories,
    categoryProductIds,
    isChild = false
  } = params;

  if (targetCategoryId == null) {
    return false;
  }

  const targetId = String(targetCategoryId);

  if (productCategoryId != null && String(productCategoryId) === targetId) {
    return true;
  }

  if (productId != null && toIdSet(categoryProductIds).has(String(productId))) {
    return true;
  }

  if (toIdSet(productCategories).has(targetId)) {
    return true;
  }

  return Boolean(isChild);
}
